import React, { useState } from 'react';
import {
  X, Check, Calculator, DollarSign, TrendingUp, Navigation, Clock,
  Truck, User, ArrowRight, Sparkles, CheckCircle2, AlertTriangle, Layers,
  ChevronRight, MapPin, Package, ShieldCheck
} from 'lucide-react';

function formatDurationSec(totalSec = 0) {
  const h = Math.floor((totalSec || 0) / 3600);
  const m = Math.floor(((totalSec || 0) % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${m}m`;
}

export default function OrderCostSimulationModal({
  order,
  simulation,
  isLoading,
  onClose,
  onAcceptAndIntegrate,
  onChangeTargetJob
}) {
  const [selectedJobIndex, setSelectedJobIndex] = useState(0);
  const [isAccepting, setIsAccepting] = useState(false);

  if (!order) return null;

  const sim = simulation || {};
  const impactedJobs = sim.impacted_jobs || [];
  const availableTargetJobs = sim.available_target_jobs || [];
  const activeJob = impactedJobs[selectedJobIndex] || impactedJobs[0] || null;

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      if (onAcceptAndIntegrate) {
        await onAcceptAndIntegrate(order.id, activeJob?.job_id);
      }
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 15, 29, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '960px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          overflow: 'hidden'
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(90deg, #131e36 0%, #0f172a 100%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'rgba(56, 189, 248, 0.15)',
                border: '1px solid #38bdf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38bdf8'
              }}
            >
              <Calculator size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800', color: '#fff' }}>
                  Simulation des Coûts & Rentabilité
                </h3>
                <span
                  style={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    color: '#38bdf8',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '700'
                  }}
                >
                  Commande #{order.id}
                </span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginTop: '2px' }}>
                Client : <strong>{order.customer_name}</strong> · Impact d'insertion dans les tournées actives
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* MODAL BODY */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <div className="spin" style={{ display: 'inline-block', marginBottom: '14px' }}>
                <Calculator size={36} color="#38bdf8" />
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: '#fff' }}>
                Calcul de l'optimisation VROOM & matrice kilométrique...
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '4px' }}>
                Évaluation de l'impact des arrêts mutualisés sur chaque mission
              </div>
            </div>
          ) : (
            <>
              {/* TOP EXECUTIVE KPI CARDS */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
                {/* 1. REVENUE */}
                <div
                  style={{
                    background: '#131e36',
                    border: '1px solid #1e3a8a',
                    borderRadius: '14px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.78rem', fontWeight: '700' }}>
                    <span>REVENU FACTURÉ</span>
                    <DollarSign size={16} color="#38bdf8" />
                  </div>
                  <div style={{ fontSize: '1.45rem', fontWeight: '900', color: '#38bdf8', marginTop: '8px' }}>
                    + {Number(sim.revenue || order.revenue || 0).toFixed(2)} <span style={{ fontSize: '0.82rem' }}>CHF</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                    Prix facturé au client donneur d'ordre
                  </div>
                </div>

                {/* 2. MARGINAL COST */}
                <div
                  style={{
                    background: '#131e36',
                    border: '1px solid #334155',
                    borderRadius: '14px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.78rem', fontWeight: '700' }}>
                    <span>SURCOÛT MARGINAL</span>
                    <Navigation size={16} color="#f59e0b" />
                  </div>
                  <div style={{ fontSize: '1.45rem', fontWeight: '900', color: '#f59e0b', marginTop: '8px' }}>
                    + {Number(sim.delta?.cost || 0).toFixed(2)} <span style={{ fontSize: '0.82rem' }}>CHF</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#cbd5e1', marginTop: '4px' }}>
                    + {Number(sim.delta?.km || 0).toFixed(1)} km · + {formatDurationSec(sim.delta?.duration_sec || 0)}
                  </div>
                </div>

                {/* 3. NET MARGIN */}
                <div
                  style={{
                    background: sim.is_profitable ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                    border: `1px solid ${sim.is_profitable ? '#166534' : '#991b1b'}`,
                    borderRadius: '14px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.78rem', fontWeight: '700' }}>
                    <span>MARGE NETTE DÉGAGÉE</span>
                    <TrendingUp size={16} color={sim.is_profitable ? '#22c55e' : '#ef4444'} />
                  </div>
                  <div style={{ fontSize: '1.45rem', fontWeight: '900', color: sim.is_profitable ? '#22c55e' : '#ef4444', marginTop: '8px' }}>
                    {sim.net_margin >= 0 ? '+' : ''}{Number(sim.net_margin || 0).toFixed(2)} <span style={{ fontSize: '0.82rem' }}>CHF</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: sim.is_profitable ? '#86efac' : '#fca5a5', marginTop: '4px', fontWeight: '700' }}>
                    Taux de marge : {Number(sim.margin_pct || 0).toFixed(1)} %
                  </div>
                </div>

                {/* 4. VERDICT */}
                <div
                  style={{
                    background: '#131e36',
                    border: `1px solid ${sim.is_profitable ? '#22c55e' : '#ef4444'}`,
                    borderRadius: '14px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#94a3b8', fontSize: '0.78rem', fontWeight: '700' }}>
                    <span>VERDICT RENTABILITÉ</span>
                    {sim.is_profitable ? <CheckCircle2 size={16} color="#22c55e" /> : <AlertTriangle size={16} color="#ef4444" />}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: '900', color: sim.is_profitable ? '#22c55e' : '#ef4444', marginTop: '8px' }}>
                    {sim.verdict || (sim.is_profitable ? 'Fortement Rentable' : 'Surcoût Élevé')}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
                    {impactedJobs.length} mission(s) impactée(s)
                  </div>
                </div>
              </div>

              {/* TARGET TOUR SELECTION BANNER */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.15) 0%, rgba(15, 23, 42, 0.6) 100%)',
                border: '1px solid #0284c7',
                borderRadius: '14px',
                padding: '14px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Truck size={18} color="#38bdf8" />
                    <span style={{ fontSize: '0.92rem', fontWeight: '800', color: '#fff' }}>
                      Tournée d'accueil assignée :
                    </span>
                    {activeJob && (
                      <span style={{ background: '#0284c7', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '800' }}>
                        {activeJob.display_name} (Job #{activeJob.job_id})
                      </span>
                    )}
                  </div>

                  {availableTargetJobs.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: '600' }}>Changer de tournée :</span>
                      <select
                        value={activeJob?.job_id || ''}
                        onChange={(e) => {
                          const jid = parseInt(e.target.value);
                          if (onChangeTargetJob) onChangeTargetJob(jid);
                        }}
                        style={{
                          background: '#0f172a',
                          border: '1px solid #38bdf8',
                          color: '#fff',
                          borderRadius: '8px',
                          padding: '5px 10px',
                          fontSize: '0.82rem',
                          fontWeight: '700',
                          cursor: 'pointer'
                        }}
                      >
                        {availableTargetJobs.map(j => (
                          <option key={j.id} value={j.id}>
                            {j.name} (Job #{j.id}) — {j.stops_count} arrêts
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {activeJob && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.78rem', color: '#cbd5e1', borderTop: '1px solid rgba(56, 189, 248, 0.2)', paddingTop: '8px', flexWrap: 'wrap' }}>
                    <span>🚚 Véhicule : <strong>{activeJob.truck_name || 'Camion'}</strong> ({activeJob.truck_plate || 'Plaque'})</span>
                    <span>👤 Chauffeur : <strong>{activeJob.driver_name || 'Non assigné'}</strong></span>
                    <span>📦 Arrêts actuels : <strong>{activeJob.current_stops_count}</strong> → <strong style={{ color: '#38bdf8' }}>{activeJob.total_stops_count}</strong> après intégration</span>
                  </div>
                )}
              </div>

              {/* IMPACTED JOBS TABS */}
              {impactedJobs.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Missions & Tournées d'accueil ({impactedJobs.length}) :
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px' }}>
                    {impactedJobs.map((j, idx) => (
                      <button
                        key={j.job_id}
                        onClick={() => setSelectedJobIndex(idx)}
                        style={{
                          background: selectedJobIndex === idx ? '#1e293b' : '#131e36',
                          border: selectedJobIndex === idx ? '1px solid #38bdf8' : '1px solid #233354',
                          borderLeft: `4px solid ${j.truck_color || '#38bdf8'}`,
                          borderRadius: '10px',
                          padding: '10px 16px',
                          color: '#fff',
                          cursor: 'pointer',
                          textAlign: 'left',
                          minWidth: '220px'
                        }}
                      >
                        <div style={{ fontSize: '0.88rem', fontWeight: '800', color: selectedJobIndex === idx ? '#38bdf8' : '#fff' }}>
                          {j.display_name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                          {j.round_name || 'Tournée'} · {j.truck_plate || 'Camion'} · +{j.added_stops_count} arrêt(s)
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ACTIVE JOB DETAILS CARD */}
              {activeJob && (
                <div
                  style={{
                    background: '#131e36',
                    border: '1px solid #233354',
                    borderRadius: '16px',
                    padding: '18px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                  }}
                >
                  {/* JOB METRICS BEFORE VS AFTER */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    <div style={{ background: '#0f172a', padding: '12px 14px', borderRadius: '10px', border: '1px solid #1e293b' }}>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>KILOMÉTRAGE</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '0.92rem' }}>
                        <span style={{ color: '#94a3b8' }}>{Number(activeJob.baseline?.km || 0).toFixed(1)} km</span>
                        <ArrowRight size={14} color="#64748b" />
                        <span style={{ color: '#fff', fontWeight: '800' }}>{Number(activeJob.simulated?.km || 0).toFixed(1)} km</span>
                        <span style={{ color: '#f59e0b', fontSize: '0.76rem', fontWeight: '700' }}>(+{Number(activeJob.delta?.km || 0).toFixed(1)} km)</span>
                      </div>
                    </div>

                    <div style={{ background: '#0f172a', padding: '12px 14px', borderRadius: '10px', border: '1px solid #1e293b' }}>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>TEMPS DE TRAVAIL</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '0.92rem' }}>
                        <span style={{ color: '#94a3b8' }}>{formatDurationSec(activeJob.baseline?.duration_sec || 0)}</span>
                        <ArrowRight size={14} color="#64748b" />
                        <span style={{ color: '#fff', fontWeight: '800' }}>{formatDurationSec(activeJob.simulated?.duration_sec || 0)}</span>
                        <span style={{ color: '#f59e0b', fontSize: '0.76rem', fontWeight: '700' }}>(+{formatDurationSec(activeJob.delta?.duration_sec || 0)})</span>
                      </div>
                    </div>

                    <div style={{ background: '#0f172a', padding: '12px 14px', borderRadius: '10px', border: '1px solid #1e293b' }}>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: '700' }}>COÛT D'EXPLOITATION</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '0.92rem' }}>
                        <span style={{ color: '#94a3b8' }}>CHF {Number(activeJob.baseline?.cost || 0).toFixed(2)}</span>
                        <ArrowRight size={14} color="#64748b" />
                        <span style={{ color: '#fff', fontWeight: '800' }}>CHF {Number(activeJob.simulated?.cost || 0).toFixed(2)}</span>
                        <span style={{ color: '#f59e0b', fontSize: '0.76rem', fontWeight: '700' }}>(+CHF {Number(activeJob.delta?.cost || 0).toFixed(2)})</span>
                      </div>
                    </div>
                  </div>

                  {/* STOPS SEQUENCE TABLE */}
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#cbd5e1', marginBottom: '8px' }}>
                      Séquence optimisée des arrêts ({activeJob.stops?.length || 0} arrêts) :
                    </div>

                    <div style={{ border: '1px solid #1e293b', borderRadius: '12px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                        <thead>
                          <tr style={{ background: '#0f172a', borderBottom: '1px solid #1e293b', color: '#94a3b8', textAlign: 'left' }}>
                            <th style={{ padding: '8px 12px', width: '40px' }}>#</th>
                            <th style={{ padding: '8px 12px' }}>Client & Adresse</th>
                            <th style={{ padding: '8px 12px', width: '100px' }}>Type</th>
                            <th style={{ padding: '8px 12px', width: '90px' }}>Arrivée</th>
                            <th style={{ padding: '8px 12px', width: '130px', textAlign: 'right' }}>Origine</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(activeJob.stops || []).map((s, sIdx) => {
                            const isNew = s.is_virtual || s.is_connected_stop;
                            return (
                              <tr
                                key={s.id || sIdx}
                                style={{
                                  background: isNew ? 'rgba(56, 189, 248, 0.12)' : sIdx % 2 === 0 ? '#131e36' : '#111b30',
                                  borderBottom: '1px solid #1e293b'
                                }}
                              >
                                <td style={{ padding: '8px 12px', fontWeight: '800', color: isNew ? '#38bdf8' : '#94a3b8' }}>
                                  {sIdx + 1}
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                  <div style={{ fontWeight: isNew ? '800' : '600', color: isNew ? '#38bdf8' : '#fff' }}>
                                    {s.client_name}
                                  </div>
                                  <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                                    {s.address}, {s.postal_code} {s.city}
                                  </div>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                  <span
                                    style={{
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontSize: '0.72rem',
                                      fontWeight: '700',
                                      background: s.type === 'pickup' ? 'rgba(2, 132, 199, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                                      color: s.type === 'pickup' ? '#38bdf8' : '#22c55e'
                                    }}
                                  >
                                    {s.type === 'pickup' ? 'Ramassage' : 'Livraison'}
                                  </span>
                                </td>
                                <td style={{ padding: '8px 12px', color: '#cbd5e1', fontWeight: '600' }}>
                                  {s.estimated_arrival ? s.estimated_arrival.slice(0, 5) : '--:--'}
                                </td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                  {isNew ? (
                                    <span
                                      style={{
                                        background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.25), rgba(14, 165, 233, 0.35))',
                                        border: '1px solid #38bdf8',
                                        color: '#38bdf8',
                                        borderRadius: '6px',
                                        padding: '2px 8px',
                                        fontSize: '0.68rem',
                                        fontWeight: '800',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <Layers size={11} />
                                      <span>MUTUALISÉ LOOPS</span>
                                    </span>
                                  ) : (
                                    <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Tournée actuelle</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div
          style={{
            padding: '18px 24px',
            borderTop: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#0a0f1d'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              color: '#cbd5e1',
              borderRadius: '10px',
              padding: '10px 20px',
              fontSize: '0.88rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Fermer
          </button>

          <button
            type="button"
            onClick={handleAccept}
            disabled={isAccepting || isLoading}
            style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              border: '1px solid #38bdf8',
              color: '#fff',
              borderRadius: '10px',
              padding: '12px 28px',
              fontSize: '0.95rem',
              fontWeight: '800',
              cursor: isAccepting || isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 15px rgba(2, 132, 199, 0.4)'
            }}
          >
            {isAccepting ? (
              <>
                <span className="spin">⚡</span>
                <span>Intégration en cours...</span>
              </>
            ) : (
              <>
                <Check size={18} />
                <span>Accepter & Intégrer dans les tournées</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
